import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { ProjetBadge } from "@/components/ProjetBadge";
import { PacListField } from "@/components/PacListField";
import { ClientInput } from "@/components/ClientInput";
import { DateInputFr } from "@/components/DateInputFr";
import { pickTier, tierLabel } from "@/lib/pricing";
import { useDraftAutosave } from "@/hooks/useDraftAutosave";
import { useAuth } from "@/context/AuthContext";
import { SECTOR_META, fmtEur, fmtMwhc } from "@/lib/format";
import { ResultSummary } from "@/components/ResultSummary";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  ArrowLeft, ArrowRight, Loader2, Check, Plus, Minus, Trash2, Home, Building2, Factory, House, Building, Tractor,
  Euro, Sparkles, ClipboardList, Save, GitCompareArrows, Sprout, Waypoints, PlusSquare, Search, Zap, Layers, MapPin, Info, ExternalLink,
  Calculator, Handshake, PenLine, HardHat, FileCheck, Wallet, Phone,
} from "lucide-react";
import { toast } from "sonner";
import { HeatNetworkIcon } from "../components/HeatNetworkIcon";

const SECTOR_ICON = { BAR: House, BAT: Building, IND: Factory, AGRI: Tractor, RES: HeatNetworkIcon };
const SECTOR_TYPES = [
  { code: "BAT", label: "Tertiaire" },
  { code: "BAR", label: "Résidentiel" },
  { code: "IND", label: "Industriel" },
  { code: "AGRI", label: "Agricole" },
  { code: "RES", label: "Réseau de chaleur" },
];
const SECTOR_LABEL = Object.fromEntries(SECTOR_TYPES.map((s) => [s.code, s.label]));

const BAT_ACTIVITIES = [
  { value: "enseignement", label: "Enseignement", factor: "bureaux_enseignement_commerces" },
  { value: "bureaux", label: "Bureaux", factor: "bureaux_enseignement_commerces" },
  { value: "commerces", label: "Commerces", factor: "bureaux_enseignement_commerces" },
  { value: "hotellerie_restauration", label: "Hôtellerie - Restauration", factor: "hotellerie_restauration" },
  { value: "sante", label: "Santé", factor: "sante" },
  { value: "autres", label: "Autres secteurs", factor: "autres" },
];
const batFactorKey = (v) => (BAT_ACTIVITIES.find((a) => a.value === v) || {}).factor || "autres";
const IND_MODES = [
  { value: "1x8", label: "1×8h" },
  { value: "2x8", label: "2×8h" },
  { value: "3x8_wknd", label: "3×8h (arrêt week-end)" },
  { value: "3x8_nonstop", label: "3×8h (sans arrêt)" },
];
const BAR_HOUSING = [
  { value: "maison", label: "Maison individuelle" },
  { value: "appartement", label: "Appartement" },
];
const AGRI_ACTIVITIES = [
  { value: "elevage", label: "Élevage" },
  { value: "cultures", label: "Grandes cultures" },
  { value: "serres", label: "Serres / Maraîchage" },
  { value: "autres", label: "Autres" },
];
const RES_TYPES = [
  { value: "chaleur", label: "Réseau de chaleur" },
  { value: "froid", label: "Réseau de froid" },
];
const STEPS = ["Configuration", "Opérations", "Paramètres techniques", "Bonifications", "Synthèse"];

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2));

function newBlock(sector) {
  const base = { id: uid(), sector, name: "" };
  if (sector === "BAT") return { ...base, activity: "enseignement", surface: "" };
  if (sector === "BAR") return { ...base, housing_type: "maison" };
  if (sector === "IND") return { ...base, operating_mode: "3x8_nonstop" };
  if (sector === "AGRI") return { ...base, activity: "elevage" };
  if (sector === "RES") return { ...base, network_type: "chaleur" };
  return base;
}

const variantName = (name) => {
  const m = /^(.*) \(variante(?: (\d+))?\)$/.exec(name);
  return m ? `${m[1]} (variante ${(Number(m[2]) || 1) + 1})` : `${name} (variante)`;
};

export default function NewSimulation() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get("id");
  const cmpId = params.get("cmp");
  const cmpMode = cmpId ? (params.get("mode") === "edit" ? "edit" : "dup") : null;
  const { user } = useAuth();
  const [bareme, setBareme] = useState(null);
  const [priceManual, setPriceManual] = useState(false);
  const keepSavedPrice = useRef(!!editId || !!cmpId);
  const allowed = user?.partner?.allowed_sectors;
  const isPartner = !!user?.partner;
  const sectorTypes = allowed ? SECTOR_TYPES.filter((s) => allowed.includes(s.code)) : SECTOR_TYPES;

  const [step, setStep] = useState(1);
  const [fiches, setFiches] = useState([]);
  const [ficheSearch, setFicheSearch] = useState("");
  const [result, setResult] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(!editId && !cmpId);
  const [loadedStatus, setLoadedStatus] = useState(null);

  const [form, setForm] = useState({
    name: "",
    client: "",
    postal_code: "",
    zone: "",
    building_over_2y: true,
    sector_blocks: [],
    engagement_date: "",
    cee_price: "",
    cee_price_precaire: "",
    cpe_enabled: false,
    cpe_coefficient: 1.5,
    cpe_duration: "",
    cpe_savings: "",
    zni_enabled: false,
    zni_coefficient: 2.0,
  });
  const [zoneInfo, setZoneInfo] = useState(null);
  const [operations, setOperations] = useState([]);
  const [infoFiche, setInfoFiche] = useState(null);

  const fichesByCode = useMemo(() => Object.fromEntries(fiches.map((f) => [f.code, f])), [fiches]);

  useEffect(() => {
    api.get("/fiches").then((r) => setFiches(r.data));
  }, []);

  useEffect(() => {
    if ((!editId && !cmpId) || fiches.length === 0) return;
    api.get(cmpId ? `/comparator/${cmpId}` : `/projects/${editId}`).then((r) => {
      const p = r.data;
      setForm({
        name: cmpMode === "dup" ? variantName(p.name) : p.name, client: p.client || "", postal_code: p.postal_code || "", zone: p.zone,
        building_over_2y: p.building_over_2y,
        sector_blocks: p.sector_blocks && p.sector_blocks.length ? p.sector_blocks : [],
        engagement_date: p.engagement_date || "",
        cee_price: p.cee_price, cee_price_precaire: p.cee_price_precaire ?? "", cpe_enabled: p.cpe_enabled, cpe_coefficient: p.cpe_coefficient || 1.5,
        zni_enabled: p.zni_enabled || false, zni_coefficient: p.zni_coefficient || 2.0,
        ...(p.cpe_duration !== undefined && { cpe_duration: p.cpe_duration }), ...(p.cpe_savings !== undefined && { cpe_savings: p.cpe_savings }),
      });
      setOperations((p.operations || []).map((o) => ({ id: uid(), block_id: o.block_id, fiche_code: o.fiche_code, name: o.name || "", bonification: o.bonification || "none", coup_de_pouce: o.coup_de_pouce || {}, zni_precarite: !!o.zni_precarite, menage_type: o.menage_type || "classique", bailleur_conventionne_100: o.bailleur_conventionne_100 ?? true, logements_precaires: o.logements_precaires ?? "", logements_total: o.logements_total ?? "", steps: o.steps || null, inputs: o.inputs, is_option: !!o.is_option })));
      if (p.postal_code) api.get(`/climate-zone/${p.postal_code}`).then((r) => setZoneInfo(r.data)).catch(() => {});
      if (p.zone) setZoneInfo({ zone: p.zone });
      setLoadedStatus(p.status || "brouillon");
      setLoaded(true);
    }).catch(() => {
      toast.error(cmpId ? "Simulation introuvable dans le comparateur" : "Projet introuvable");
      navigate(cmpId ? "/comparateur" : "/projects");
    });
  }, [editId, cmpId, cmpMode, fiches]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Pré-remplissage depuis le catalogue (?fiche=CODE) : crée le secteur + l'opération et va aux paramètres
  const prefilledRef = useRef(false);
  useEffect(() => {
    if (editId || cmpId || prefilledRef.current || fiches.length === 0) return;
    const code = params.get("fiche");
    if (!code) return;
    const fiche = fichesByCode[code];
    if (!fiche) return;
    prefilledRef.current = true;
    const block = newBlock(fiche.sector);
    const inputs = {};
    fiche.fields.forEach((fl) => (inputs[fl.key] = fl.type === "number" ? "" : fl.default));
    const op = { id: uid(), block_id: block.id, fiche_code: fiche.code, name: "", bonification: "none", coup_de_pouce: {}, zni_precarite: false, menage_type: "classique", bailleur_conventionne_100: true, logements_precaires: "", logements_total: "" };
    if (fiche.supports_steps) { op.steps = [{ ...inputs, step_name: "" }]; op.inputs = {}; }
    else { op.steps = null; op.inputs = inputs; }
    setForm((f) => ({ ...f, sector_blocks: [block] }));
    setOperations([op]);
    setStep(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fiches, editId]);

  // Barème : pré-remplit le prix selon la date d'engagement et le palier de volume du dossier
  useEffect(() => {
    const date = form.engagement_date || new Date().toISOString().slice(0, 10);
    api.get(`/price-schedule/resolve?date=${date}`).then((r) => {
      setBareme(r.data || null);
      if (keepSavedPrice.current) {
        keepSavedPrice.current = false;
        setPriceManual(true);
      } else if (r.data) setPriceManual(false);
    }).catch(() => {});
  }, [form.engagement_date]);

  const dossierMwhc = (result?.total_kwhc || 0) / 1000;
  const tier = pickTier(bareme, dossierMwhc);
  useEffect(() => {
    if (!tier || priceManual) return;
    setForm((f) => {
      const pp = tier.cee_price_precaire ?? "";
      if (String(f.cee_price) === String(tier.cee_price) && String(f.cee_price_precaire) === String(pp)) return f;
      return { ...f, cee_price: tier.cee_price, cee_price_precaire: pp };
    });
  }, [tier?.min_mwhc, tier?.cee_price, tier?.cee_price_precaire, priceManual]); // eslint-disable-line react-hooks/exhaustive-deps


  const onPostal = (v) => {
    set("postal_code", v);
    if (v.length >= 2) {
      api.get(`/climate-zone/${v}`).then((r) => {
        setZoneInfo(r.data);
        set("zone", r.data.zone);
      });
    } else setZoneInfo(null);
  };

  const addBlock = (sector) => setForm((f) => ({ ...f, sector_blocks: [...f.sector_blocks, newBlock(sector)] }));
  const removeBlock = (id) => {
    setForm((f) => ({ ...f, sector_blocks: f.sector_blocks.filter((b) => b.id !== id) }));
    setOperations((ops) => ops.filter((o) => o.block_id !== id));
  };
  const setBlockField = (id, key, val) => {
    if (key === "surface") {
      const old = form.sector_blocks.find((b) => b.id === id)?.surface ?? "";
      const sync = (inp) => (inp && "surface" in inp && (inp.surface === "" || String(inp.surface) === String(old)) ? { ...inp, surface: val } : inp);
      setOperations((ops) => ops.map((o) => (o.block_id !== id || !fichesByCode[o.fiche_code]?.fields.some((fl) => fl.key === "surface" && fl.from_block) ? o
        : { ...o, inputs: sync(o.inputs), steps: o.steps ? o.steps.map(sync) : o.steps })));
    }
    setForm((f) => ({ ...f, sector_blocks: f.sector_blocks.map((b) => (b.id === id ? { ...b, [key]: val } : b)) }));
  };

  const defaultInputs = (fiche, block) => {
    const inputs = {};
    fiche.fields.forEach((fl) => (inputs[fl.key] = fl.type === "number" ? "" : fl.default));
    if (fiche.code === "BAR-TH-171" && block.housing_type) inputs.logement = block.housing_type;
    if (fiche.code === "IND-UT-134" && block.operating_mode) inputs.fonctionnement = block.operating_mode;
    if (block.surface && fiche.fields.some((fl) => fl.key === "surface" && fl.from_block)) inputs.surface = block.surface;
    return inputs;
  };

  const addOperation = (block, fiche, isOption = false) => {
    const inputs = defaultInputs(fiche, block);
    const op = { id: uid(), block_id: block.id, fiche_code: fiche.code, name: "", bonification: "none", coup_de_pouce: {}, zni_precarite: false, menage_type: "classique", bailleur_conventionne_100: true, logements_precaires: "", logements_total: "", is_option: isOption };
    if (fiche.supports_steps) {
      op.steps = [{ ...inputs, step_name: "" }];
      op.inputs = {};
    } else {
      op.steps = null;
      op.inputs = inputs;
    }
    setOperations((ops) => [...ops, op]);
    toast.success(`${fiche.code} ajoutée${isOption ? " en option" : ""} à « ${block.name || SECTOR_LABEL[block.sector]} »`);
  };

  const addStep = (opId, fiche, block) =>
    setOperations((ops) => ops.map((o) => (o.id === opId ? { ...o, steps: [...(o.steps || []), { ...defaultInputs(fiche, block), step_name: "" }] } : o)));
  const removeStep = (opId, idx) =>
    setOperations((ops) => ops.map((o) => (o.id === opId ? { ...o, steps: (o.steps || []).filter((_, i) => i !== idx) } : o)));
  const setStepInput = (opId, idx, key, val) =>
    setOperations((ops) => ops.map((o) => (o.id === opId ? { ...o, steps: (o.steps || []).map((s, i) => (i === idx ? { ...s, [key]: val } : s)) } : o)));
  const removeOperation = (opId) => setOperations((ops) => ops.filter((o) => o.id !== opId));
  const setOpInput = (opId, key, val) =>
    setOperations((ops) => ops.map((o) => (o.id === opId ? { ...o, inputs: { ...o.inputs, [key]: val } } : o)));
  const setOpName = (opId, val) => setOperations((ops) => ops.map((o) => (o.id === opId ? { ...o, name: val } : o)));
  const setOpCdp = (opId, key, val) =>
    setOperations((ops) => ops.map((o) => (o.id === opId ? { ...o, coup_de_pouce: { ...(o.coup_de_pouce || {}), [key]: val } } : o)));
  const setOpBonif = (opId, val) => setOperations((ops) => ops.map((o) => (o.id === opId ? { ...o, bonification: val } : o)));
  const setOpZniPrec = (opId, val) => setOperations((ops) => ops.map((o) => (o.id === opId ? { ...o, zni_precarite: val } : o)));
  const setOpMenage = (opId, patch) => setOperations((ops) => ops.map((o) => (o.id === opId ? { ...o, ...patch } : o)));
  const setCpeEnabled = (val) => {
    set("cpe_enabled", val);
    if (!val) setOperations((ops) => ops.map((o) => (o.bonification === "cpe" ? { ...o, bonification: "none" } : o)));
  };

  const opCount = (blockId, code) => operations.filter((o) => o.block_id === blockId && o.fiche_code === code).length;
  const decOperation = (blockId, code) =>
    setOperations((ops) => {
      let lastIdx = -1;
      ops.forEach((o, i) => {
        if (o.block_id === blockId && o.fiche_code === code) lastIdx = i;
      });
      if (lastIdx === -1) return ops;
      return ops.filter((_, i) => i !== lastIdx);
    });

  const blockName = (b) => b.name || SECTOR_LABEL[b.sector];

  const buildOperations = () =>
    operations.map((o) => {
      const block = form.sector_blocks.find((b) => b.id === o.block_id);
      const sa = block && block.sector === "BAT" ? batFactorKey(block.activity) : undefined;
      return { fiche_code: o.fiche_code, inputs: o.inputs, steps: o.steps || null, name: o.name || "", bonification: o.bonification || "none", coup_de_pouce: o.coup_de_pouce || {}, zni_precarite: !!o.zni_precarite, menage_type: block && block.sector === "BAR" ? (o.menage_type || "classique") : "classique", bailleur_conventionne_100: o.bailleur_conventionne_100 ?? true, logements_precaires: o.logements_precaires === "" ? null : o.logements_precaires, logements_total: o.logements_total === "" ? null : o.logements_total, sector_activity: sa, bat_activity: block && block.sector === "BAT" ? block.activity : undefined, block_id: o.block_id, block_name: block ? blockName(block) : "", is_option: !!o.is_option };
    });

  const payload = () => ({ ...form, operations: buildOperations() });
  const isZni = zoneInfo?.is_zni || result?.is_zni || false;
  const cpeCoef = (form.cpe_duration || form.cpe_savings)
    ? Math.round((1 + (Number(form.cpe_duration) >= 10 ? 3 : 2) * (Number(form.cpe_savings || 0) / 100)) * 1000) / 1000
    : Number(form.cpe_coefficient) || 1;

  const calcSeq = useRef(0);
  const calculate = async () => {
    const seq = ++calcSeq.current;
    setCalculating(true);
    try {
      const { data } = await api.post("/calculate", payload());
      if (seq === calcSeq.current) setResult(data);
    } catch {
      toast.error("Erreur de calcul");
    } finally {
      if (seq === calcSeq.current) setCalculating(false);
    }
  };

  useEffect(() => {
    if (operations.length === 0) {
      setResult(null);
      return;
    }
    const t = setTimeout(() => calculate(), 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(operations), form.cee_price, form.cee_price_precaire, form.cpe_enabled, form.cpe_coefficient, form.zni_enabled, form.zni_coefficient, JSON.stringify(form.sector_blocks), form.zone]);

  const hasContent = !!(form.name.trim() || form.postal_code.trim() || form.sector_blocks.length || operations.length);
  const draft = useDraftAutosave(
    { ...payload(), name: form.name.trim() || "Brouillon sans titre", status: loadedStatus || "brouillon" },
    { ready: loaded, enabled: hasContent && !cmpId, initialId: editId },
  );

  const [comparing, setComparing] = useState(false);
  const addToComparator = async () => {
    if (!operations.length) {
      toast.error("Ajoutez au moins une opération");
      return;
    }
    setComparing(true);
    try {
      const id = await draft.finalize();
      const draftId = id && (!editId || loadedStatus === "brouillon") ? id : undefined;
      if (cmpMode === "edit") {
        await api.put(`/comparator/${cmpId}`, { ...payload(), name: form.name.trim() || "Simulation" });
        toast.success("Simulation mise à jour dans le comparateur");
        navigate("/comparateur");
        return;
      }
      const { data: list } = await api.get("/comparator");
      const name = form.name.trim() || `Simulation ${list.length + 1}`;
      await api.post("/comparator", { ...payload(), name }, { params: draftId ? { draft_id: draftId } : {} });
      toast.success(`« ${name} » ajoutée au comparateur (${list.length + 1})`, { action: { label: "Voir", onClick: () => navigate("/comparateur") } });
      navigate(cmpMode ? "/comparateur" : "/simulation/new");
    } catch (e) {
      draft.resume();
      toast.error(e.response?.data?.detail || "Échec de l'ajout au comparateur");
    } finally {
      setComparing(false);
    }
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Le nom du projet est requis");
      setStep(1);
      return;
    }
    setSaving(true);
    try {
      const id = (await draft.finalize()) || editId;
      const body = { ...payload(), status: "finalisé" };
      const { data } = id ? await api.put(`/projects/${id}`, body) : await api.post("/projects", body);
      toast.success(editId ? "Projet mis à jour" : "Projet enregistré");
      navigate(`/projects/${data.id}`);
    } catch {
      draft.resume();
      toast.error("Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const next = () => {
    if (step === 1 && (!form.name.trim() || !form.postal_code.trim())) {
      toast.error("Renseignez le nom du projet et le code postal");
      return;
    }
    if (step === 1 && form.sector_blocks.length === 0) {
      toast.error("Ajoutez au moins un secteur");
      return;
    }
    if (step === 2 && operations.length === 0) {
      toast.error("Ajoutez au moins une opération");
      return;
    }
    setStep((s) => Math.min(5, s + 1));
  };
  const prev = () => setStep((s) => Math.max(1, s - 1));

  const renderFicheRow = (f, bidx, b) => {
    const count = opCount(b.id, f.code);
    const optCount = operations.filter((o) => o.block_id === b.id && o.fiche_code === f.code && o.is_option).length;
    return (
      <div key={f.code} className={`flex items-center justify-between gap-3 rounded-lg border p-3 transition-colors ${count > 0 ? "border-genis/40 bg-genis/5" : "border-border"}`} data-testid={`op-card-${bidx}-${f.code}`}>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <Badge className={`${SECTOR_META[f.sector].badge} border text-[10px]`}>{f.code}</Badge><ProjetBadge show={f.projet} code={f.code} />
            {f.coup_de_pouce && (
              <span className="inline-flex items-center gap-1 rounded-full bg-green-100 text-green-700 px-2 py-0.5 text-[10px] font-semibold" data-testid={`badge-cdp-${f.code}`}>
                <Zap className="h-2.5 w-2.5" /> Coup de pouce
              </span>
            )}
            {f.multi_sector && (
              <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 text-purple-700 px-2 py-0.5 text-[10px] font-semibold" data-testid={`badge-multi-${f.code}`}>
                <Layers className="h-2.5 w-2.5" /> Multi-secteur
              </span>
            )}
          </div>
          <div className="text-sm font-medium leading-snug mt-1">{f.name}</div>
          <div className="text-xs font-medium text-genis-dark dark:text-genis truncate" data-testid={`prime-hint-row-${f.code}`}>{f.prime_hint || f.unit_hint}</div>
          <div className="flex items-center gap-3 mt-1">
            <button onClick={() => setInfoFiche(f)} className="inline-flex items-center gap-1 text-genis text-xs font-semibold hover:underline" data-testid={`info-fiche-${f.code}`}>
              <Info className="h-3 w-3" /> En savoir plus
            </button>
            {optCount > 0 && <span className="text-[11px] font-semibold text-amber-700" data-testid={`op-option-count-${bidx}-${f.code}`}>dont {optCount} en option</span>}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Button variant="outline" size="icon" className="h-8 w-8" disabled={count === 0} onClick={() => decOperation(b.id, f.code)} data-testid={`dec-op-${bidx}-${f.code}`}>
            <Minus className="h-4 w-4" />
          </Button>
          <span className="w-8 text-center metric font-bold text-genis-deep dark:text-white" data-testid={`op-count-${bidx}-${f.code}`}>{count}</span>
          <Button size="icon" className="h-8 w-8 bg-genis hover:bg-genis-dark text-white" onClick={() => addOperation(b, f)} title="Ajouter au projet" data-testid={`add-op-${bidx}-${f.code}`}>
            <Plus className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" className="h-8 px-2.5 border-amber-400 text-amber-700 hover:bg-amber-50 hover:text-amber-800 text-xs font-semibold" onClick={() => addOperation(b, f, true)} title="Ajouter en option (hors total ferme)" data-testid={`add-option-${bidx}-${f.code}`}>
            <Plus className="h-3.5 w-3.5 mr-0.5" /> Option
          </Button>
        </div>
      </div>
    );
  };

  const renderField = (fl, value, onChange, tid, allInputs) => {
    if (fl.show_if && allInputs) {
      const ok = Object.entries(fl.show_if).every(([k, v]) => String(allInputs[k]) === String(v));
      if (!ok) return null;
    }
    if (fl.type === "pac_list") return <PacListField key={fl.key} fl={fl} value={value} onChange={onChange} tid={tid} allInputs={allInputs} />;
    return (
    <div key={fl.key} className={`space-y-1.5 ${fl.type === "boolean" ? "sm:col-span-2" : ""}`}>
      {fl.type === "boolean" ? (
        <div className="flex items-center justify-between rounded-lg border border-border p-3">
          <div>
            <div className="text-sm font-medium">{fl.label}</div>
            {fl.help && <div className="text-xs text-muted-foreground">{fl.help}</div>}
          </div>
          <Switch checked={!!value} onCheckedChange={(v) => onChange(v)} data-testid={tid} />
        </div>
      ) : (
        <>
          <Label className="text-sm">{fl.label}{fl.unit ? ` (${fl.unit})` : ""}</Label>
          {fl.type === "select" ? (
            <Select value={String(value)} onValueChange={(v) => onChange(v)}>
              <SelectTrigger className="h-auto min-h-10 text-left whitespace-normal [&>span]:line-clamp-none" data-testid={tid}><SelectValue /></SelectTrigger>
              <SelectContent>
                {fl.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <Input type="number" value={value} onChange={(e) => onChange(e.target.value)} placeholder="0" data-testid={tid} />
          )}
          {fl.help && <p className="text-xs text-muted-foreground">{fl.help}</p>}
        </>
      )}
    </div>
  );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-genis">{cmpMode === "edit" ? "Modification d'une simulation du comparateur" : cmpMode === "dup" ? "Variante d'une simulation du comparateur" : editId ? (loadedStatus === "brouillon" ? "Reprise du brouillon" : "Modification") : "Nouvelle simulation"}</p>
          <h1 className="text-3xl font-extrabold font-heading tracking-tight text-genis-deep dark:text-white">Simulateur CEE</h1>
        </div>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-1 lg:hidden">
        {STEPS.map((label, i) => {
          const n = i + 1;
          const active = n === step;
          const done = n < step;
          return (
            <div key={label} className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => n < step && setStep(n)}
                className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                  active ? "bg-genis text-white" : done ? "bg-genis/10 text-genis-dark" : "bg-secondary text-muted-foreground"
                }`}
                data-testid={`step-indicator-${n}`}
              >
                <span className={`h-5 w-5 grid place-items-center rounded-full text-xs ${active ? "bg-white/25" : done ? "bg-genis text-white" : "bg-white/60"}`}>
                  {done ? <Check className="h-3 w-3" /> : n}
                </span>
                <span className="hidden sm:inline">{label}</span>
              </button>
              {n < STEPS.length && <div className="h-px w-4 bg-border" />}
            </div>
          );
        })}
      </div>

      {/* Sticky band */}
      <div className="sticky top-16 z-40 mb-6">
        <div className="gradient-genis text-white rounded-xl px-4 sm:px-6 py-3 flex items-center justify-between gap-3 shadow-lg">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[10px] uppercase tracking-wider text-white/70 hidden md:block">Projet</span>
            <span className="font-heading font-bold truncate">{form.name || "Nouvelle simulation"}</span>
            {draft.savedAt && (
              <span className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-medium text-white/90 shrink-0" title="Brouillon enregistré" data-testid="draft-saved-indicator">
                <Check className="h-3 w-3" /><span className="hidden lg:inline">Brouillon enregistré à</span> {draft.savedAt.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 sm:gap-6 shrink-0">
            <div className="text-right hidden sm:block">
              <div className="text-[10px] uppercase tracking-wider text-white/70">Montant total</div>
              <div className="metric font-bold leading-tight" data-testid="band-total-prime">{fmtEur(result?.total_prime || 0)}</div>
            </div>
            <div className="text-right hidden sm:block">
              <div className="text-[10px] uppercase tracking-wider text-white/70">Volume total</div>
              <div className="metric font-bold leading-tight" data-testid="band-total-mwhc">{fmtMwhc(result?.total_kwhc || 0)}</div>
            </div>
            {step === 5 ? (
              <Button onClick={save} disabled={saving} className="bg-lime-300 text-genis-deep hover:bg-lime-200 font-semibold" data-testid="band-next-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> {editId ? "Mettre à jour" : "Enregistrer"}</>}
              </Button>
            ) : (
              <Button onClick={next} className="bg-lime-300 text-genis-deep hover:bg-lime-200 font-semibold" data-testid="band-next-btn">
                {step === 3 ? "Voir les bonifications" : "Suivant"} <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        <aside className="hidden lg:block lg:col-span-3 lg:sticky lg:top-24">
          <div className="space-y-1">
            {STEPS.map((label, i) => {
              const n = i + 1;
              const active = n === step;
              const done = n < step;
              return (
                <div key={label}>
                  <button onClick={() => setStep(n)} className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${active ? "bg-genis-deep text-white" : "hover:bg-secondary"}`} data-testid={`side-step-${n}`}>
                    <span className={`h-6 w-6 grid place-items-center rounded-full text-xs shrink-0 ${done ? "bg-genis text-white" : active ? "bg-white/20" : "bg-secondary text-muted-foreground border border-border"}`}>
                      {done ? <Check className="h-3.5 w-3.5" /> : n}
                    </span>
                    <span className="font-heading font-semibold text-sm">{label}</span>
                  </button>
                  {n === 2 && step >= 2 && form.sector_blocks.length > 0 && (
                    <div className="ml-6 mt-1 border-l border-border pl-3 space-y-1 pb-2">
                      {form.sector_blocks.map((b) => {
                        const bOps = operations.filter((o) => o.block_id === b.id);
                        return (
                          <div key={b.id}>
                            <div className="text-xs font-semibold text-muted-foreground py-1">{blockName(b)}</div>
                            {bOps.map((o) => (
                              <div key={o.id} className={`flex items-center gap-1.5 text-xs py-0.5 ${o.is_option ? "rounded-md bg-amber-50 border border-dashed border-amber-300 px-1.5 my-0.5 text-amber-800" : "text-genis-deep dark:text-white"}`} data-testid={`side-op-${o.id}`}>
                                {o.is_option ? <PlusSquare className="h-3 w-3 text-amber-600 shrink-0" /> : <Check className="h-3 w-3 text-green-500 shrink-0" />}
                                <span className="truncate">{o.op_name || fichesByCode[o.fiche_code]?.name}</span>
                                {o.is_option && <span className="ml-auto shrink-0 rounded bg-amber-200/70 px-1 text-[9px] font-bold uppercase tracking-wider" data-testid={`side-op-option-${o.id}`}>Option</span>}
                              </div>
                            ))}
                          </div>
                        );
                      })}
                      <button onClick={() => setStep(2)} className="inline-flex items-center gap-1.5 text-genis text-xs font-semibold py-1" data-testid="side-add-op">
                        <Plus className="h-3.5 w-3.5" /> Ajouter une opération
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </aside>

        <div className="lg:col-span-6 space-y-6">
          {/* STEP 1 */}
          {step === 1 && (
            <div className="space-y-6 animate-fade-up">
              <Card className="p-6 space-y-5" data-testid="step-1-panel">
                <h3 className="font-heading font-bold text-lg text-genis-deep dark:text-white">Mon projet</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nom du projet *</Label>
                    <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Ex : Décarbonation d'un hôtel 4 étoiles" data-testid="input-project-name" />
                  </div>
                  <div className="space-y-2">
                    <Label>Client</Label>
                    <ClientInput value={form.client} onChange={(v) => set("client", v)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Code postal *</Label>
                    <Input value={form.postal_code} onChange={(e) => onPostal(e.target.value)} placeholder="75009" maxLength={5} data-testid="input-postal-code" />
                  </div>
                  <div className="space-y-2">
                    <Label>Zone climatique</Label>
                    <div className="h-10 flex items-center gap-2 rounded-md border border-input px-3 bg-secondary/40">
                      {form.zone ? (
                        <span className="text-sm font-semibold text-genis-deep dark:text-white" data-testid="zone-display">{form.zone}</span>
                      ) : (
                        <span className="text-sm text-muted-foreground">Auto</span>
                      )}
                      {zoneInfo?.description && <span className="text-xs text-muted-foreground truncate">— {zoneInfo.description}</span>}
                    </div>
                    {zoneInfo?.eligibility_message && <p className="rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-xs font-semibold text-red-700" data-testid="territory-ineligible">{zoneInfo.eligibility_message}</p>}
                    {zoneInfo?.territory_note && <p className="text-xs text-blue-700" data-testid="territory-note">{zoneInfo.territory_note}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date d'engagement des travaux</Label>
                    <DateInputFr value={form.engagement_date} onChange={(v) => set("engagement_date", v)} testid="input-engagement-date" />
                    <p className="text-xs text-muted-foreground" data-testid="engagement-date-help">Date d'acceptation du devis ou du bon de commande par le bénéficiaire (date de signature), sauf disposition particulière de la fiche. Elle détermine la version de fiche applicable, les Coups de pouce en vigueur et le prix des CEE repris en synthèse.</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>L'opération est réalisée dans un bâtiment existant depuis plus de 2 ans</Label>
                  <RadioGroup
                    value={form.building_over_2y ? "oui" : "non"}
                    onValueChange={(v) => set("building_over_2y", v === "oui")}
                    className="flex items-center gap-6 pt-1"
                    data-testid="radio-building-2y"
                  >
                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                      <RadioGroupItem value="oui" data-testid="radio-building-oui" /> Oui
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                      <RadioGroupItem value="non" data-testid="radio-building-non" /> Non
                    </label>
                  </RadioGroup>
                </div>
              </Card>

              <Card className="p-6 space-y-5" data-testid="step-1-sectors-panel">
                <div>
                  <h3 className="font-heading font-bold text-lg text-genis-deep dark:text-white">Secteur(s)</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Choisissez un secteur ci-dessous pour démarrer. Vous pourrez en ajouter d'autres ensuite. Ces choix détermineront la liste des opérations CEE disponibles.
                  </p>
                </div>

                {form.sector_blocks.length === 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="sector-picker">
                    {sectorTypes.map((s) => {
                      const Icon = SECTOR_ICON[s.code];
                      return (
                        <button
                          key={s.code}
                          type="button"
                          onClick={() => addBlock(s.code)}
                          data-testid={`pick-sector-${s.code}`}
                          className="group relative flex flex-col items-center justify-center gap-3 rounded-2xl border border-border bg-white dark:bg-secondary/30 px-4 py-7 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-lg hover:border-genis focus:outline-none focus-visible:ring-2 focus-visible:ring-genis"
                        >
                          <span className="relative grid place-items-center h-12 w-12">
                            <span className="absolute right-1 top-0 h-8 w-8 rounded-full bg-genis-accent/30 transition-colors duration-200 group-hover:bg-genis-accent/50" />
                            <Icon className="relative h-8 w-8 text-genis-deep dark:text-white" strokeWidth={1.75} />
                          </span>
                          <span className="text-sm font-heading font-bold text-genis-deep dark:text-white text-center leading-tight">{s.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                <div className="space-y-6">
                  {form.sector_blocks.map((b, idx) => {
                    const Icon = SECTOR_ICON[b.sector];
                    return (
                      <div key={b.id} data-testid={`sector-block-${b.sector}-${idx}`}>
                        {idx > 0 && <Separator className="mb-6" />}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="h-7 w-7 grid place-items-center rounded-md" style={{ background: `${SECTOR_META[b.sector]?.color || "#33ADF5"}18`, color: SECTOR_META[b.sector]?.color || "#33ADF5" }}>
                              <Icon className="h-4 w-4" />
                            </span>
                            <span className="font-heading font-bold text-genis-deep dark:text-white">{SECTOR_LABEL[b.sector]}</span>
                          </div>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:bg-destructive/10" onClick={() => removeBlock(b.id)} data-testid={`remove-block-${idx}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Nom du secteur</Label>
                            <Input value={b.name} onChange={(e) => setBlockField(b.id, "name", e.target.value)} placeholder="Bâtiment A, bâtiment B, …" data-testid={`block-name-${idx}`} />
                          </div>

                          {b.sector === "BAT" && (
                            <>
                              <div className="space-y-2">
                                <Label>Secteur d'activité</Label>
                                <Select value={b.activity} onValueChange={(v) => setBlockField(b.id, "activity", v)}>
                                  <SelectTrigger data-testid={`block-activity-${idx}`}><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    {BAT_ACTIVITIES.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-2">
                                <Label>Surface totale chauffée (m²)</Label>
                                <div className="relative">
                                  <Input type="number" value={b.surface} onChange={(e) => setBlockField(b.id, "surface", e.target.value)} placeholder="298,8" className="pr-10" data-testid={`block-surface-${idx}`} />
                                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">m²</span>
                                </div>
                              </div>
                            </>
                          )}

                          {b.sector === "BAR" && (
                            <div className="space-y-2">
                              <Label>Type de logement</Label>
                              <Select value={b.housing_type} onValueChange={(v) => setBlockField(b.id, "housing_type", v)}>
                                <SelectTrigger data-testid={`block-housing-${idx}`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {BAR_HOUSING.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                          )}

                          {b.sector === "IND" && (
                            <div className="space-y-2">
                              <Label>Mode de fonctionnement du site</Label>
                              <Select value={b.operating_mode} onValueChange={(v) => setBlockField(b.id, "operating_mode", v)}>
                                <SelectTrigger data-testid={`block-mode-${idx}`}><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                                <SelectContent>
                                  {IND_MODES.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                          )}

                          {b.sector === "AGRI" && (
                            <div className="space-y-2">
                              <Label>Type d'exploitation</Label>
                              <Select value={b.activity} onValueChange={(v) => setBlockField(b.id, "activity", v)}>
                                <SelectTrigger data-testid={`block-agri-${idx}`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {AGRI_ACTIVITIES.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                          )}

                          {b.sector === "RES" && (
                            <div className="space-y-2">
                              <Label>Type de réseau</Label>
                              <Select value={b.network_type} onValueChange={(v) => setBlockField(b.id, "network_type", v)}>
                                <SelectTrigger data-testid={`block-res-${idx}`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {RES_TYPES.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="inline-flex items-center gap-2 text-genis font-semibold text-sm hover:text-genis-dark" data-testid="add-sector-btn">
                      <PlusSquare className="h-5 w-5" /> Ajouter un secteur
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-52">
                    {sectorTypes.map((s) => {
                      const Icon = SECTOR_ICON[s.code];
                      return (
                        <DropdownMenuItem key={s.code} onClick={() => addBlock(s.code)} data-testid={`add-sector-${s.code}`}>
                          <Icon className="h-4 w-4 mr-2 text-genis" /> {s.label}
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              </Card>
            </div>
          )}

          {/* BONIFICATIONS */}
          {step === 4 && (
            <Card className="p-6 space-y-5 animate-fade-up" data-testid="step-2-panel">
              <div>
                <h3 className="font-heading font-bold text-lg text-genis-deep dark:text-white">Bonifications</h3>
                <p className="text-sm text-muted-foreground mt-1">Les bonifications ne sont pas cumulables : une seule bonification par opération.</p>
              </div>

              <div className="space-y-4">
                <div className="rounded-lg border border-border p-4">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2.5">
                      <span className="h-9 w-9 grid place-items-center rounded-lg bg-bonus-cpe/10 text-bonus-cpe"><Handshake className="h-4.5 w-4.5" /></span>
                      <div>
                        <div className="text-sm font-semibold text-genis-deep dark:text-white">Contrat de Performance Énergétique (CPE)</div>
                        <div className="text-xs text-muted-foreground">Le projet inclut-il un CPE ?</div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {[{ v: false, l: "Non" }, { v: true, l: "Oui" }].map((o) => (
                        <button key={o.l} onClick={() => setCpeEnabled(o.v)} className={`rounded-full px-4 py-1.5 text-sm font-semibold border transition-colors ${form.cpe_enabled === o.v ? "bg-bonus-cpe text-white border-bonus-cpe" : "bg-secondary text-muted-foreground border-transparent hover:text-foreground"}`} data-testid={`cpe-toggle-${o.v ? "oui" : "non"}`}>
                          {o.l}
                        </button>
                      ))}
                    </div>
                  </div>
                  {form.cpe_enabled && (
                    <div className="mt-4 pt-4 border-t border-border space-y-2" data-testid="cpe-fields">
                      <div className="flex items-end gap-3 flex-wrap">
                        <div className="space-y-1">
                          <span className="text-xs text-muted-foreground">Durée du contrat (ans)</span>
                          <Input type="number" min="1" value={form.cpe_duration} onChange={(e) => set("cpe_duration", e.target.value)} className="w-28" placeholder="12" data-testid="input-cpe-duration" />
                        </div>
                        <div className="space-y-1">
                          <span className="text-xs text-muted-foreground">Économie garantie (%)</span>
                          <Input type="number" min="0" max="100" value={form.cpe_savings} onChange={(e) => set("cpe_savings", e.target.value)} className="w-28" placeholder="25" data-testid="input-cpe-savings" />
                        </div>
                        <div className="rounded-lg bg-bonus-cpe/10 text-bonus-cpe px-3 py-2 text-sm font-bold metric" data-testid="cpe-coef-display">
                          Coefficient CPE ×{cpeCoef}
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">Coefficient = 1 + N × E (N = 2 si &lt; 10 ans, 3 si ≥ 10 ans ; E = % garanti). Choisissez ensuite les fiches concernées via le bouton « CPE » sur chaque opération ci-dessous.</p>
                    </div>
                  )}
                </div>
                {isZni && (
                  <div className="rounded-lg border border-border p-3 text-sm flex items-center gap-2">
                    <MapPin className="h-4 w-4 shrink-0 text-pink-600" />
                    <span>Projet en <span className="font-semibold text-pink-600">Zone Non-Interconnectée</span> — bonification ×2 (×3 grande précarité) disponible.</span>
                  </div>
                )}
              </div>

              {operations.length === 0 && <p className="text-sm text-muted-foreground">Aucune opération. Ajoutez des opérations à l'étape précédente.</p>}

              {form.sector_blocks.map((b) => {
                const blockOps = operations.filter((o) => o.block_id === b.id);
                if (blockOps.length === 0) return null;
                return (
                  <div key={b.id} className="space-y-3">
                    <div className="text-sm font-semibold text-genis-deep dark:text-white">{blockName(b)}{b.name ? ` · ${SECTOR_LABEL[b.sector]}` : ""}</div>
                    {blockOps.map((op) => {
                      const fiche = fichesByCode[op.fiche_code];
                      if (!fiche) return null;
                      const cdp = fiche.coup_de_pouce;
                      const choices = [
                        { v: "none", label: "Aucune", show: true },
                        { v: "coup_de_pouce", label: "Coup de pouce", show: !!cdp },
                        { v: "zni", label: "ZNI", show: isZni },
                        { v: "cpe", label: "CPE", show: !!form.cpe_enabled && !cdp },
                      ].filter((o) => o.show);
                      return (
                        <div key={op.id} className="rounded-xl border border-border p-4 space-y-3" data-testid={`bonif-op-${op.id}`}>
                          <div className="flex items-center gap-2">
                            <Badge className={`${SECTOR_META[fiche.sector].badge} border text-[10px]`}>{fiche.code}</Badge><ProjetBadge show={fiche.projet} code={fiche.code} />
                            <span className="font-semibold text-sm">{op.name || fiche.name}</span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {choices.map((o) => (
                              <button key={o.v} onClick={() => setOpBonif(op.id, o.v)} className={`rounded-full px-3 py-1.5 text-sm font-medium border transition-colors ${op.bonification === o.v ? "bg-genis text-white border-genis" : "bg-secondary text-muted-foreground border-transparent hover:text-foreground"}`} data-testid={`bonif-${op.id}-${o.v}`}>
                                {o.label}
                              </button>
                            ))}
                          </div>

                          {op.bonification === "coup_de_pouce" && cdp && (
                            <div className="rounded-lg border border-green-200 bg-green-50 p-3 space-y-3">
                              {cdp.options ? (
                                <div className="space-y-1.5">
                                  <Label className="text-sm text-green-800">{cdp.options.some((o) => o.prime_per_m2) ? "Nature des travaux (montant de la prime)" : "Type d'équipement (coefficient)"}</Label>
                                  <Select value={op.coup_de_pouce?.type || ""} onValueChange={(v) => setOpCdp(op.id, "type", v)}>
                                    <SelectTrigger className="bg-white h-auto min-h-10 text-left whitespace-normal [&>span]:line-clamp-none" data-testid={`cdp-type-${op.id}`}><SelectValue placeholder={cdp.options.some((o) => o.prime_per_m2) ? "Choisir la nature des travaux…" : "Choisir le coefficient…"} /></SelectTrigger>
                                    <SelectContent>
                                      {cdp.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.prime_per_m2 ? o.label : `${o.label} — ×${o.factor}`}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                </div>
                              ) : (
                                <div className="space-y-1">
                                  <div className="text-sm font-semibold text-green-800" data-testid={`cdp-fixed-${op.id}`}>{cdp.label}{cdp.factor ? ` ×${cdp.factor}` : ""}</div>
                                  {cdp.factors && (
                                    <div className="text-xs text-green-800" data-testid={`cdp-factor-auto-${op.id}`}>
                                      Coefficient appliqué : <span className="font-bold">×{["precaire", "bailleur"].includes(op.menage_type) ? cdp.factors.modeste : cdp.factors.autres}</span> ({["precaire", "bailleur"].includes(op.menage_type) ? "ménage modeste" : "autres ménages"}) — {cdp.factor_label}
                                    </div>
                                  )}
                                  {cdp.note && <div className="text-[11px] text-green-700" data-testid={`cdp-note-${op.id}`}>{cdp.note}</div>}
                                  {cdp.period && (() => {
                                    const eng = form.engagement_date || new Date().toISOString().slice(0, 10);
                                    const msg = (eng < cdp.period[0] || eng > cdp.period[1]) ? "Date d'engagement hors période : bonification non appliquée."
                                      : (cdp.metropole_only && isZni) ? "Hors France métropolitaine : bonification non appliquée." : null;
                                    return msg && <div className="rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-800" data-testid={`cdp-warning-${op.id}`}>{msg}</div>;
                                  })()}
                                </div>
                              )}
                              <div className="space-y-2">
                                {cdp.conditions.map((c) => (
                                  <label key={c.key} className="flex items-start gap-2 text-xs text-green-900 cursor-pointer">
                                    <Checkbox checked={!!op.coup_de_pouce?.[c.key]} onCheckedChange={(v) => setOpCdp(op.id, c.key, !!v)} className="mt-0.5 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" data-testid={`cdp-${op.id}-${c.key}`} />
                                    <span>{c.label}{c.required && <span className="text-green-600 font-bold"> *</span>}</span>
                                  </label>
                                ))}
                              </div>
                              {cdp.options && cdp.note && <div className="text-[11px] text-green-700" data-testid={`cdp-options-note-${op.id}`}>{cdp.note}</div>}
                              {cdp.options && cdp.metropole_only && isZni && <div className="rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-800" data-testid={`cdp-warning-${op.id}`}>Hors France métropolitaine : bonification non appliquée.</div>}
                              {!((cdp.requires || cdp.conditions.filter((c) => c.required).map((c) => c.key)).every((k) => op.coup_de_pouce?.[k]) && (!cdp.options || op.coup_de_pouce?.type)) && (
                                <p className="text-[11px] text-green-700" data-testid={`cdp-hint-${op.id}`}>{cdp.options ? (cdp.options.some((o) => o.prime_per_m2) ? "Sélectionnez la nature des travaux et cochez les conditions requises (*)." : "Sélectionnez le type d'équipement et cochez les conditions requises (*).") : "Cochez les conditions requises (*) pour appliquer la bonification."}</p>
                              )}
                              {cdp.options?.some((o) => o.prime_per_m2) && <p className="text-[11px] text-green-700">La prime Coup de pouce ne dépend pas du prix CEE.</p>}
                            </div>
                          )}

                          {op.bonification === "zni" && isZni && (
                            <label className="flex items-center gap-2 text-sm rounded-lg border border-pink-200 bg-pink-50 p-3 cursor-pointer">
                              <Checkbox checked={!!op.zni_precarite} onCheckedChange={(v) => setOpZniPrec(op.id, !!v)} data-testid={`zni-prec-${op.id}`} />
                              <span>Ménages en grande précarité énergétique — bonification <span className="font-semibold">×3</span> (sinon ×2)</span>
                            </label>
                          )}

                          {op.bonification === "cpe" && (
                            <div className="text-sm text-muted-foreground rounded-lg bg-secondary/50 p-3">Bonification CPE ×{cpeCoef} appliquée à cette opération.</div>
                          )}

                          {b.sector === "BAR" && (
                            <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3 space-y-3" data-testid={`menage-op-${op.id}`}>
                              <div className="text-sm font-semibold text-indigo-900">Type de ménage (précarité énergétique)</div>
                              <div className="flex flex-wrap gap-2">
                                {[{ v: "classique", label: "Ménage classique" }, { v: "precaire", label: "Ménage précarité" }, { v: "bailleur", label: "Bailleur social" }].map((m) => (
                                  <button key={m.v} onClick={() => setOpMenage(op.id, { menage_type: m.v })} className={`rounded-full px-3 py-1.5 text-sm font-medium border transition-colors ${(op.menage_type || "classique") === m.v ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-muted-foreground border-transparent hover:text-foreground"}`} data-testid={`menage-${op.id}-${m.v}`}>
                                    {m.label}
                                  </button>
                                ))}
                              </div>
                              {op.menage_type && op.menage_type !== "classique" && (
                                <div className="text-xs text-indigo-800">
                                  Taux de précarité (colonne A) — département {result?.department || zoneInfo?.department || "—"} : <span className="font-semibold">{result?.precarite_pct ?? "—"} %</span>
                                </div>
                              )}
                              {op.menage_type === "bailleur" && (
                                <div className="space-y-2">
                                  <label className="flex items-center gap-2 text-xs text-indigo-900 cursor-pointer">
                                    <Checkbox checked={op.bailleur_conventionne_100 !== false} onCheckedChange={(v) => setOpMenage(op.id, { bailleur_conventionne_100: !!v })} data-testid={`bailleur-100-${op.id}`} />
                                    <span>Bailleur 100 % conventionné (sinon saisir le ratio de logements précaires)</span>
                                  </label>
                                  {op.bailleur_conventionne_100 === false && (
                                    <div className="grid grid-cols-2 gap-2">
                                      <div className="space-y-1">
                                        <Label className="text-xs text-indigo-800">Logements précaires</Label>
                                        <Input type="number" value={op.logements_precaires} onChange={(e) => setOpMenage(op.id, { logements_precaires: e.target.value })} className="bg-white" data-testid={`log-prec-${op.id}`} />
                                      </div>
                                      <div className="space-y-1">
                                        <Label className="text-xs text-indigo-800">Logements total</Label>
                                        <Input type="number" value={op.logements_total} onChange={(e) => setOpMenage(op.id, { logements_total: e.target.value })} className="bg-white" data-testid={`log-total-${op.id}`} />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </Card>
          )}

          {/* OPÉRATIONS */}
          {step === 2 && (
            <Card className="p-6 space-y-5 animate-fade-up" data-testid="step-3-panel">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h3 className="font-heading font-bold text-lg text-genis-deep dark:text-white">Choix des opérations</h3>
                  <p className="text-sm text-muted-foreground">Sélectionnez les fiches par secteur et le nombre d'opérations souhaité.</p>
                </div>
                <Badge variant="outline" className="w-fit">{operations.length} opération(s)</Badge>
              </div>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input value={ficheSearch} onChange={(e) => setFicheSearch(e.target.value)} placeholder="Rechercher une fiche (code, nom, catégorie)…" className="pl-9" data-testid="fiche-search-input" />
              </div>

              <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5"><span className="inline-flex items-center gap-1 rounded-full bg-green-100 text-green-700 px-2 py-0.5 font-semibold"><Zap className="h-3 w-3" /> Coup de pouce</span> éligible</span>
                <span className="inline-flex items-center gap-1.5"><span className="inline-flex items-center gap-1 rounded-full bg-purple-100 text-purple-700 px-2 py-0.5 font-semibold"><Layers className="h-3 w-3" /> Multi-secteur</span> plusieurs secteurs</span>
              </div>

              {form.sector_blocks.map((b, bidx) => {
                const Icon = SECTOR_ICON[b.sector];
                const q = ficheSearch.trim().toLowerCase();
                const available = fiches
                  .filter((f) => (f.sectors || [f.sector]).includes(b.sector))
                  .filter((f) => !q || f.code.toLowerCase().includes(q) || f.name.toLowerCase().includes(q) || f.category.toLowerCase().includes(q));
                const categories = [...new Set(available.map((f) => f.category))];
                const blockOpsCount = operations.filter((o) => o.block_id === b.id).length;

                return (
                  <div key={b.id} className="rounded-xl border border-border overflow-hidden" data-testid={`fiche-block-${bidx}`}>
                    <div className="flex items-center justify-between bg-secondary/50 px-4 py-3 border-b border-border">
                      <div className="flex items-center gap-2">
                        <span className="h-7 w-7 grid place-items-center rounded-md" style={{ background: `${SECTOR_META[b.sector]?.color || "#33ADF5"}18`, color: SECTOR_META[b.sector]?.color || "#33ADF5" }}>
                          <Icon className="h-4 w-4" />
                        </span>
                        <span className="font-semibold">{blockName(b)}</span>
                        {b.name && <Badge variant="outline" className="text-[10px]">{SECTOR_LABEL[b.sector]}</Badge>}
                      </div>
                      {blockOpsCount > 0 && <span className="text-xs font-semibold text-genis">{blockOpsCount} sélectionnée(s)</span>}
                    </div>

                    {available.length === 0 ? (
                      <div className="text-sm text-muted-foreground p-4">{q ? "Aucune fiche ne correspond à la recherche." : "Fiches à venir pour ce secteur."}</div>
                    ) : (
                      <div className="divide-y divide-border">
                        {categories.map((cat) => (
                          <div key={cat} className="p-4">
                            <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2">{cat}</div>
                            <div className="space-y-2">
                              {available.filter((f) => f.category === cat).map((f) => renderFicheRow(f, bidx, b))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </Card>
          )}

          {/* PARAMÈTRES */}
          {step === 3 && (
            <Card className="p-6 space-y-5 animate-fade-up" data-testid="step-4-panel">
              <h3 className="font-heading font-bold text-lg text-genis-deep dark:text-white">Paramètres techniques</h3>
              {operations.length === 0 && <p className="text-muted-foreground text-sm">Aucune opération. Revenez à l'étape précédente.</p>}
              {form.sector_blocks.map((b) => {
                const blockOps = operations.filter((o) => o.block_id === b.id);
                if (blockOps.length === 0) return null;
                return (
                  <div key={b.id} className="space-y-3">
                    <div className="text-sm font-semibold text-genis-deep dark:text-white">{blockName(b)}{b.name ? ` · ${SECTOR_LABEL[b.sector]}` : ""}</div>
                    {blockOps.map((op) => {
                      const fiche = fichesByCode[op.fiche_code];
                      if (!fiche) return null;
                      const meta = SECTOR_META[fiche.sector];
                      const sameCode = blockOps.filter((x) => x.fiche_code === op.fiche_code);
                      const occ = sameCode.length > 1 ? ` #${sameCode.indexOf(op) + 1}` : "";
                      return (
                        <div key={op.id} className="rounded-xl border border-border p-4" data-testid={`op-params-${op.id}`}>
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <Badge className={`${meta.badge} border text-[10px]`}>{fiche.code}</Badge><ProjetBadge show={fiche.projet} code={fiche.code} />
                              <span className="font-semibold text-sm">{fiche.name}{occ}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <label className={`flex items-center gap-2 rounded-full border px-3 h-8 text-xs font-semibold cursor-pointer transition-colors ${op.is_option ? "border-amber-400 bg-amber-50 text-amber-700" : "border-border text-muted-foreground"}`} title="Opération optionnelle : chiffrée à part, hors total ferme">
                                <Switch checked={!!op.is_option} onCheckedChange={(v) => setOpMenage(op.id, { is_option: v })} className="scale-75" data-testid={`option-switch-${op.id}`} />
                                Option
                              </label>
                              <Button variant="ghost" size="sm" className="text-genis h-8" onClick={() => setInfoFiche(fiche)} data-testid={`info-op-${op.id}`}>
                                <Info className="h-4 w-4 mr-1" /> En savoir plus
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => removeOperation(op.id)} data-testid={`remove-op-${op.id}`}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>

                          <div className="space-y-1.5 mb-4">
                            <Label className="text-sm">Nom de l'opération</Label>
                            <Input value={op.name || ""} onChange={(e) => setOpName(op.id, e.target.value)} placeholder={`Ex : ${fiche.name}${occ}`} data-testid={`op-name-${op.id}`} />
                          </div>

                          {op.steps ? (
                            <div className="space-y-3">
                              {op.steps.map((st, si) => (
                                <div key={si} className="rounded-lg border border-border bg-secondary/30 p-3" data-testid={`calc-step-${op.id}-${si}`}>
                                  <div className="flex items-center justify-between gap-2 mb-2">
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                      <span className="text-xs font-semibold text-genis shrink-0">Étape {si + 1}</span>
                                      <Input value={st.step_name || ""} onChange={(e) => setStepInput(op.id, si, "step_name", e.target.value)} placeholder="Nom de l'équipement (ex : PAC 1)" className="h-8 max-w-xs" data-testid={`step-name-${op.id}-${si}`} />
                                    </div>
                                    {op.steps.length > 1 && (
                                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive shrink-0" onClick={() => removeStep(op.id, si)} data-testid={`remove-step-${op.id}-${si}`}>
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </div>
                                  <div className="grid sm:grid-cols-2 gap-4">
                                    {fiche.fields.map((fl) => renderField(fl, st[fl.key], (v) => setStepInput(op.id, si, fl.key, v), `field-${op.id}-${si}-${fl.key}`, st))}
                                  </div>
                                </div>
                              ))}
                              <button onClick={() => addStep(op.id, fiche, b)} className="inline-flex items-center gap-2 text-genis font-semibold text-sm hover:text-genis-dark" data-testid={`add-step-${op.id}`}>
                                <PlusSquare className="h-4 w-4" /> Ajouter une étape de calcul
                              </button>
                            </div>
                          ) : (
                            <div className="grid sm:grid-cols-2 gap-4">
                              {fiche.fields.map((fl) => renderField(fl, op.inputs[fl.key], (v) => setOpInput(op.id, fl.key, v), `field-${op.id}-${fl.key}`, op.inputs))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </Card>
          )}

          {/* STEP 5 */}
          {step === 5 && (
            <Card className="p-6 space-y-4 animate-fade-up" data-testid="step-5-panel">
              <div className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5 text-genis" />
                <h3 className="font-heading font-bold text-lg text-genis-deep dark:text-white">Synthèse de la simulation</h3>
              </div>
              <div className="rounded-xl border border-genis/30 bg-genis/5 p-4 space-y-3" data-testid="synthese-prix">
                <div className="flex items-center gap-2">
                  <Euro className="h-4 w-4 text-genis" />
                  <span className="font-heading font-semibold text-sm text-genis-deep dark:text-white">Prix des CEE</span>
                  {form.engagement_date && <Badge variant="outline" className="text-[10px] ml-auto">Barème au {new Date(form.engagement_date).toLocaleDateString("fr-FR")}</Badge>}
                </div>
                <div className={`grid grid-cols-1 ${result?.has_precarite ? "sm:grid-cols-2" : ""} gap-3`}>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Prix classique (€/MWhc)</Label>
                    <Input type="number" step="0.01" value={isPartner ? (result?.cee_price ?? form.cee_price) : form.cee_price} readOnly={isPartner} className={isPartner ? "bg-secondary/60 cursor-not-allowed" : ""} onChange={(e) => { setPriceManual(true); set("cee_price", e.target.value); }} placeholder="Prix du barème ou saisie manuelle" data-testid="input-cee-price" />
                  </div>
                  {result?.has_precarite && (
                    <div className="space-y-1.5">
                      <Label className="text-xs">Prix précarité (€/MWhc)</Label>
                      <Input type="number" step="0.01" value={isPartner ? (result?.cee_price_precaire ?? "") : form.cee_price_precaire} readOnly={isPartner} className={isPartner ? "bg-secondary/60 cursor-not-allowed" : ""} onChange={(e) => { setPriceManual(true); set("cee_price_precaire", e.target.value); }} placeholder="Optionnel" data-testid="input-cee-price-precaire" />
                    </div>
                  )}
                </div>
                {bareme && tierLabel(bareme, tier) && (
                  <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="price-tier-info">
                    <Badge className="bg-genis text-white" data-testid="price-tier-badge">Palier {tierLabel(bareme, tier)}</Badge>
                    <span className="text-muted-foreground">Volume du dossier : {dossierMwhc.toLocaleString("fr-FR", { maximumFractionDigits: 1 })} MWhc</span>
                  </div>
                )}
                {isPartner && (
                  <p className="text-[11px] font-medium text-genis-dark" data-testid="partner-price-note">
                    {bareme ? "Prix fixé par Genis Energie selon le barème de votre espace et le palier de volume du dossier." : "Aucun tarif n'est encore défini pour votre espace : contactez Genis Energie."}
                  </p>
                )}
                {!isPartner && priceManual && bareme && (
                  <button type="button" onClick={() => setPriceManual(false)} className="text-xs font-semibold text-genis hover:underline" data-testid="price-reset-bareme">
                    Prix modifié manuellement · revenir au prix du barème
                  </button>
                )}
                {!isPartner && !form.engagement_date && <p className="text-[11px] text-muted-foreground">Renseignez une date d'engagement à l'étape Configuration pour reprendre automatiquement le tarif du barème (et son palier de volume).</p>}
                <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 flex gap-2.5" data-testid="prix-disclaimer">
                  <Info className="h-4 w-4 shrink-0 text-amber-600 mt-0.5" />
                  <div className="text-[12px] text-amber-900 dark:text-amber-200 leading-relaxed">
                    <span className="font-semibold">Prix de rachat indicatif et non contractuel.</span> Contactez Genis Energie pour bloquer un prix de rachat <span className="font-semibold">ferme et définitif</span>. Le prix peut évoluer selon le volume de CEE, les dates de début et de fin de chantier, le type de fiche et le secteur d'activité.
                  </div>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                {[
                  ["Projet", form.name || "—"],
                  ["Zone", form.zone || "—"],
                  ["Prix CEE", `${form.cee_price || 0} €/MWhc`],
                  ["Secteurs", form.sector_blocks.length],
                  ["Opérations", operations.length],
                  ["Bonifications", `${result ? (result.operations || []).filter((o) => o.bonification && o.bonification !== "none").length : operations.filter((o) => o.bonification && o.bonification !== "none").length} opération(s) bonifiée(s)`],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between rounded-lg bg-secondary/50 px-3 py-2">
                    <span className="text-muted-foreground">{k}</span>
                    <span className="font-medium">{v}</span>
                  </div>
                ))}
              </div>
              <Button onClick={calculate} variant="outline" className="w-full hover:border-genis hover:text-genis" data-testid="recalculate-btn">
                {calculating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Recalculer"}
              </Button>
              <Button onClick={save} disabled={saving} className="w-full bg-genis hover:bg-genis-dark text-white h-11" data-testid="save-project-btn">
                {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> {editId ? "Mettre à jour le projet" : "Enregistrer le projet"}</>}
              </Button>
              <Button onClick={addToComparator} disabled={comparing} variant="outline" className="w-full h-11 border-genis/50 text-genis hover:bg-genis/10 hover:text-genis" data-testid="add-to-comparator-btn">
                {comparing ? <Loader2 className="h-5 w-5 animate-spin" /> : <><GitCompareArrows className="h-4 w-4 mr-1.5" /> {cmpMode === "edit" ? "Mettre à jour dans le comparateur" : cmpMode === "dup" ? "Ajouter la variante au comparateur" : "Ajouter au comparateur et enchaîner"}</>}
              </Button>
            </Card>
          )}

          {step === 5 && (
            <div className="rounded-2xl bg-genis-deep p-2 sm:p-2.5 overflow-hidden" data-testid="contact-banner">
              <div className="grid lg:grid-cols-[1.15fr_1fr] gap-2.5">
                <div className="rounded-xl bg-white/[0.06] ring-1 ring-white/10 p-4 sm:p-5">
                  <div className="grid grid-cols-3 gap-y-5 gap-x-2">
                    {[
                      { icon: Calculator, label: "Calcul du montant de la prime", active: true },
                      { icon: Handshake, label: "Contractualisation avec Genis Energie" },
                      { icon: PenLine, label: "Signature des commandes travaux" },
                      { icon: HardHat, label: "Réalisation des travaux" },
                      { icon: FileCheck, label: "Finalisation du dossier CEE" },
                      { icon: Wallet, label: "Versement de la prime CEE" },
                    ].map((s, i) => {
                      const Ico = s.icon;
                      return (
                        <div key={i} className="flex flex-col items-center text-center gap-2">
                          <span className={`h-11 w-11 grid place-items-center rounded-full ring-1 ${s.active ? "bg-lime-300/15 ring-lime-300 text-lime-300" : "bg-white/5 ring-white/15 text-white/40"}`}>
                            <Ico className="h-5 w-5" />
                          </span>
                          <span className={`text-[11px] leading-tight ${s.active ? "text-lime-300 font-semibold" : "text-white/50"}`}>{s.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="p-4 sm:p-5 flex flex-col justify-center">
                  <h3 className="font-heading font-extrabold text-2xl text-white leading-tight">Contractualisez vos CEE</h3>
                  <p className="text-sm text-white/70 mt-2">Prenez rendez-vous avec votre conseiller Genis Energie pour signer une convention et garantir le montant de rachat de vos primes CEE.</p>
                  <a
                    href={`mailto:contact@genis-energie.fr?subject=${encodeURIComponent(`Offre de rachat CEE — ${form.name || "Nouveau projet"}`)}&body=${encodeURIComponent(`Bonjour,\n\nJe souhaite une offre définitive de rachat pour ma simulation CEE${result?.total_prime ? ` (prime estimée : ${Math.round(result.total_prime).toLocaleString("fr-FR")} €)` : ""}.\n\nMerci de me recontacter.`)}`}
                    className="mt-4 inline-flex items-center gap-2 self-start rounded-full bg-lime-300 text-genis-deep hover:bg-lime-200 font-semibold px-5 py-2.5 text-sm transition-colors"
                    data-testid="contact-cta"
                  >
                    <Phone className="h-4 w-4" /> Prendre rendez-vous
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Nav buttons */}
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={step === 1 ? () => navigate(-1) : prev} data-testid="wizard-prev-btn">
              <ArrowLeft className="h-4 w-4 mr-1" /> {step === 1 ? "Annuler" : "Précédent"}
            </Button>
            {step < 5 && (
              <Button onClick={next} className="bg-genis hover:bg-genis-dark text-white" data-testid="wizard-next-btn">
                Suivant <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        </div>

        {/* Right summary */}
        <div className="lg:col-span-3 lg:sticky lg:top-24 space-y-4">
          {result ? (
            <ResultSummary result={result} />
          ) : (
            <Card className="p-6 text-center">
              <div className="mx-auto h-14 w-14 grid place-items-center rounded-2xl bg-genis/10 text-genis mb-3">
                <Euro className="h-7 w-7" />
              </div>
              <h4 className="font-heading font-bold text-genis-deep dark:text-white">Estimation en attente</h4>
              <p className="text-sm text-muted-foreground mt-1">Ajoutez vos secteurs et opérations, puis atteignez la synthèse pour obtenir le montant de la prime.</p>
              {operations.length > 0 && (
                <div className="mt-4 space-y-2 text-left">
                  {operations.map((op) => (
                    <div key={op.id} className="flex items-center gap-2 text-sm rounded-lg bg-secondary/50 px-3 py-2">
                      <Badge className={`${SECTOR_META[fichesByCode[op.fiche_code]?.sector]?.badge || ""} border text-[10px]`}>{op.fiche_code}</Badge><ProjetBadge show={fichesByCode[op.fiche_code]?.projet} code={op.fiche_code} />
                      <span className="truncate">{fichesByCode[op.fiche_code]?.name}</span>
                      {op.is_option && <Badge variant="outline" className="ml-auto border-amber-400 text-amber-700 text-[10px]">Option</Badge>}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}
        </div>
      </div>

      <Sheet open={!!infoFiche} onOpenChange={(o) => !o && setInfoFiche(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          {infoFiche && (
            <div className="space-y-5">
              <SheetHeader>
                <SheetTitle className="font-heading text-xl leading-snug text-left">{infoFiche.name}</SheetTitle>
                <SheetDescription className="sr-only">Détails de la fiche d'opération standardisée {infoFiche.code}</SheetDescription>
              </SheetHeader>
              <div className="flex items-center justify-between">
                <Badge className={`${SECTOR_META[infoFiche.sector].badge} border`}>{infoFiche.code}</Badge>
                <a href={`https://www.google.com/search?q=fiche+CEE+${infoFiche.code}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-genis text-sm font-semibold hover:underline" data-testid="info-voir-fiche">
                  <ExternalLink className="h-4 w-4" /> Voir la fiche
                </a>
              </div>
              <div className="rounded-xl gradient-genis text-white p-4">
                <div className="font-heading font-bold">Besoin d'aide ?</div>
                <p className="text-sm text-white/85 mt-1">Pour toute question, contactez Genis pour une assistance rapide et efficace.</p>
                <a href="mailto:contact@genis-energie.fr" className="inline-block mt-3 rounded-full bg-white/20 px-3 py-1.5 text-sm font-semibold hover:bg-white/30 transition-colors">contact@genis-energie.fr</a>
              </div>
              <div>
                <div className="font-heading font-bold text-genis-deep dark:text-white mb-2">Points d'attention</div>
                <div className="space-y-2">
                  <div className="rounded-lg bg-secondary/60 p-3">
                    <div className="font-semibold text-sm">Catégorie</div>
                    <div className="text-sm text-muted-foreground">{infoFiche.category} · {infoFiche.sector_label}</div>
                  </div>
                  <div className="rounded-lg bg-secondary/60 p-3">
                    <div className="font-semibold text-sm">Description</div>
                    <div className="text-sm text-muted-foreground">{infoFiche.description}</div>
                  </div>
                  <div className="rounded-lg bg-secondary/60 p-3">
                    <div className="font-semibold text-sm">Formule de calcul</div>
                    <div className="text-sm text-muted-foreground">{infoFiche.unit_hint}</div>
                  </div>
                  {infoFiche.eligibility?.length > 0 && (
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-3" data-testid="info-eligibility">
                      <div className="font-semibold text-sm text-blue-800">Conditions d'éligibilité</div>
                      <ul className="mt-1 space-y-1 text-sm text-blue-700 list-disc pl-4">
                        {infoFiche.eligibility.map((e, i) => <li key={i}>{e}</li>)}
                      </ul>
                    </div>
                  )}
                  {infoFiche.exclusions?.length > 0 && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3" data-testid="info-exclusions">
                      <div className="font-semibold text-sm text-red-800">Exclusions & points de vigilance</div>
                      <ul className="mt-1 space-y-1 text-sm text-red-700 list-disc pl-4">
                        {infoFiche.exclusions.map((e, i) => <li key={i}>{e}</li>)}
                      </ul>
                    </div>
                  )}
                  {infoFiche.coup_de_pouce && (
                    <div className="rounded-lg bg-green-50 border border-green-200 p-3">
                      <div className="font-semibold text-sm text-green-800">Coup de pouce éligible</div>
                      <div className="text-sm text-green-700">{infoFiche.coup_de_pouce.options ? `Bonification ×${infoFiche.coup_de_pouce.options.map((o) => o.factor).join(" / ")} selon l'équipement installé.` : infoFiche.coup_de_pouce.factors ? `Bonification ${infoFiche.coup_de_pouce.factor_label}. ${infoFiche.coup_de_pouce.note}` : infoFiche.coup_de_pouce.factor ? `Bonification ×${infoFiche.coup_de_pouce.factor} sous conditions.` : "Bonification selon une formule dédiée (surface / nombre de logements), sous conditions."}</div>
                    </div>
                  )}
                  {infoFiche.multi_sector && (
                    <div className="rounded-lg bg-purple-50 border border-purple-200 p-3">
                      <div className="font-semibold text-sm text-purple-800">Fiche multi-secteur</div>
                      <div className="text-sm text-purple-700">Utilisable dans plusieurs secteurs d'activité.</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
